import React, { useRef, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Paper,
    Grid,
    Card,
    Stack,
    useMediaQuery,
    Chip,
    Divider,
    CircularProgress,
    Skeleton,
    Fade,
    Fab,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

import LoginIcon from "@mui/icons-material/Login";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import WeekendRoundedIcon from "@mui/icons-material/WeekendRounded";
import BedtimeRoundedIcon from "@mui/icons-material/BedtimeRounded";
import HomeWorkRoundedIcon from "@mui/icons-material/HomeWorkRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import PixIcon from "@mui/icons-material/Pix";
import AssuredWorkloadRoundedIcon from "@mui/icons-material/AssuredWorkloadRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

const CTA_GREEN = "#16A34A";
const CTA_GREEN_HOVER = "#15803D";
// const BRAND_PURPLE = "#7F72F7";
const BRAND_TEAL = "#128C7E";

const TopSalesBar = ({ onPrimaryClick }) => {
    return (
        <Box
            sx={(theme) => ({
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1400,
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1, sm: 1.15 },
                backdropFilter: "blur(14px)",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.50)}`,
                bgcolor:
                    theme.palette.mode === "dark"
                        ? alpha(theme.palette.background.paper, 0.88)
                        : alpha("#ffffff", 0.9),
                boxShadow: `0 10px 32px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.36 : 0.10)}`,
            })}
        >
            <Box
                sx={{
                    maxWidth: 1200,
                    mx: "auto",
                    display: "flex",
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: "space-between",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 1, sm: 1.5 },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, minWidth: 0 }}>
                    <Box
                        sx={(theme) => ({
                            width: 36,
                            height: 36,
                            borderRadius: 2.4,
                            display: "grid",
                            placeItems: "center",
                            color: "#fff",
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${BRAND_TEAL} 100%)`,
                            boxShadow: `0 12px 26px ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.22)}`,
                            flexShrink: 0,
                        })}
                    >
                        <BoltRoundedIcon sx={{ fontSize: 21 }} />
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontWeight: 950,
                                fontSize: { xs: 13.5, sm: 14.5 },
                                color: "text.primary",
                                lineHeight: 1.15,
                                whiteSpace: { xs: "normal", sm: "nowrap" },
                            }}
                        >
                            Chega de levar planejamento para casa.
                        </Typography>

                        <Typography
                            sx={{
                                mt: 0.2,
                                fontSize: { xs: 12.2, sm: 13.2 },
                                color: "text.secondary",
                                lineHeight: 1.2,
                                whiteSpace: { xs: "normal", md: "nowrap" },
                            }}
                        >
                            Crie sua conta e receba 1 teste gratuito para ver a Docen funcionando na prática.
                        </Typography>
                    </Box>
                </Box>

                <Button
                    onClick={onPrimaryClick}
                    variant="contained"
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                        borderRadius: 999,
                        px: { xs: 2, sm: 2.4 },
                        py: 1,
                        fontWeight: 950,
                        textTransform: "none",
                        bgcolor: CTA_GREEN,
                        boxShadow: "none",
                        whiteSpace: "nowrap",
                        "&:hover": {
                            bgcolor: CTA_GREEN_HOVER,
                            boxShadow: "none",
                        },
                    }}
                >
                    Testar grátis
                </Button>
            </Box>
        </Box>
    );
};

const SectionHeader = ({ eyebrow, title, description, align = "center", maxWidth = 720 }) => (
    <Box
        sx={{
            textAlign: align,
            maxWidth,
            mx: align === "center" ? "auto" : 0,
            mb: { xs: 2.2, sm: 3 },
        }}
    >
        {eyebrow && (
            <Chip
                size="small"
                label={eyebrow}
                sx={(theme) => ({
                    mb: 1.2,
                    height: 27,
                    fontWeight: 900,
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.16 : 0.08),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                })}
            />
        )}

        <Typography
            sx={{
                fontSize: { xs: 25, sm: 33, md: 38 },
                lineHeight: 1.08,
                letterSpacing: -1,
                fontWeight: 950,
                color: "text.primary",
            }}
        >
            {title}
        </Typography>

        {description && (
            <Typography
                sx={{
                    mt: 1.2,
                    color: "text.secondary",
                    fontSize: { xs: 14.8, sm: 16.2 },
                    lineHeight: 1.55,
                }}
            >
                {description}
            </Typography>
        )}
    </Box>
);

const VideoFrame = ({ title, src, loaded, onLoad, aspect = "16 / 9", squareOnDesktop = false, clean = false }) => (
    <Box
        sx={(theme) => ({
            width: "100%",
            borderRadius: clean ? 3 : 4,
            overflow: "hidden",
            border: clean ? "none" : `1px solid ${alpha(theme.palette.divider, 0.85)}`,
            bgcolor: clean ? "transparent" : "background.paper",
            boxShadow: clean
                ? "none"
                : `0 22px 60px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.28 : 0.10)}`,
        })}
    >
        <Box
            sx={{
                position: "relative",
                width: "100%",
                aspectRatio: squareOnDesktop ? { xs: "9 / 16", md: "1 / 1" } : aspect,
                bgcolor: "background.default",
                borderRadius: clean ? 3 : 0,
                overflow: "hidden",
            }}
        >
            <Fade in={!loaded} timeout={200} unmountOnExit>
                <Box sx={{ position: "absolute", inset: 0, zIndex: 2 }}>
                    <Skeleton variant="rectangular" width="100%" height="100%" />
                    <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
                        <CircularProgress />
                    </Box>
                </Box>
            </Fade>

            <iframe
                title={title}
                src={src}
                allow="fullscreen; picture-in-picture"
                allowFullScreen
                loading="lazy"
                onLoad={onLoad}
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                    display: "block",
                }}
            />
        </Box>
    </Box>
);

const FeatureLine = ({ children, muted = false, color = "primary.main" }) => {
    const isLightText = color === "#fff" || color === "white";
    const match = typeof children === "string" ? children.match(/^(\d+)(.*)$/) : null;

    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <CheckCircleRoundedIcon
                sx={{
                    mt: "2px",
                    fontSize: 18,
                    color: muted ? "text.disabled" : color,
                    flexShrink: 0,
                }}
            />
            <Typography
                sx={{
                    fontSize: 14.5,
                    lineHeight: 1.45,
                    color: muted ? "text.disabled" : isLightText ? alpha("#fff", 0.92) : "text.secondary",
                    textDecoration: muted ? "line-through" : "none",
                }}
            >
                {match ? (
                    <>
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 950,
                                color: muted ? "text.disabled" : isLightText ? "#fff" : "text.primary",
                                textDecoration: muted ? "line-through" : "underline",
                            }}
                        >
                            {match[1]}
                        </Box>
                        {match[2]}
                    </>
                ) : (
                    children
                )}
            </Typography>
        </Box>
    );
};

const RevealOnScroll = ({
    children,
    delay = 0,
    direction = "up",
    distance = 28,
    once = true,
    sx = {},
}) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    React.useEffect(() => {
        const node = ref.current;
        if (!node) return;

        const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

        if (reduceMotion) {
            setVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);

                    if (once) {
                        observer.unobserve(entry.target);
                    }
                } else if (!once) {
                    setVisible(false);
                }
            },
            {
                threshold: 0.16,
                rootMargin: "0px 0px -8% 0px",
            }
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, [once]);

    const hiddenTransform = {
        up: `translate3d(0, ${distance}px, 0)`,
        down: `translate3d(0, -${distance}px, 0)`,
        left: `translate3d(${distance}px, 0, 0)`,
        right: `translate3d(-${distance}px, 0, 0)`,
        none: "translate3d(0, 0, 0)",
    };

    return (
        <Box
            ref={ref}
            sx={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translate3d(0, 0, 0)" : hiddenTransform[direction],
                transition: `opacity 650ms ease ${delay}ms, transform 650ms cubic-bezier(.2,.8,.2,1) ${delay}ms`,
                willChange: "opacity, transform",
                ...sx,
            }}
        >
            {children}
        </Box>
    );
};

export default function LandingPage() {
    const theme = useTheme();
    const navigate = useNavigate();

    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const plansRef = useRef(null);

    const [topVideoLoaded, setTopVideoLoaded] = useState(false);
    const [depVideosLoaded, setDepVideosLoaded] = useState({});

    const BUNNY_LIBRARY_ID = process.env.REACT_APP_LIBRARY_ID;
    const BUNNY_VIDEO_TOPO = process.env.REACT_APP_VIDEO_TOPO;

    const DEPOIMENTOS_VIDEOS = [
        {
            bunny_id: process.env.REACT_APP_DEPOIMENTO_2,
            title: "Relato de professora (30s)",
        },
        {
            bunny_id: process.env.REACT_APP_DEPOIMENTO_1,
            title: "Relato de professora (1 min)",
        },
    ].filter((item) => Boolean(item.bunny_id));

    const whatsappURL = "https://wa.me/5524998359874?text=Ol%C3%A1%2C+gostaria+de+saber+mais+sobre+a+plataforma+Docen!";

    const goToCadastro = () => navigate("/cadastro");

    const scrollToPlans = () => {
        const el = document.getElementById("plans-section");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const markDepLoaded = (id) => {
        setDepVideosLoaded((prev) => ({ ...prev, [id]: true }));
    };

    const IS_TOPBAR = true;

    const PLANS = [
        {
            tipo: "PROFESSOR",
            title: "Professor",
            price: "R$29,90/mês",
            badge: "Essencial",
            desc: "Para começar a reduzir o trabalho manual.",
            features: [
                "10 documentos com IA por mês",
                "Atividades, avaliações e planos de aula",
                "Alinhamento à BNCC",
                "Edição antes de exportar",
                "Exportação em Word e PDF",
                "Exportação do gabarito",
                "Suporte por WhatsApp",
            ],
            highlight: false,
        },
        {
            tipo: "MENTOR",
            title: "Mentor",
            price: "R$49,90/mês",
            badge: "Mais escolhido",
            desc: "Para quem produz materiais com frequência.",
            features: [
                "25 documentos com IA por mês",
                "Atividades, avaliações e planos de aula",
                "Alinhamento à BNCC",
                "Edição antes de exportar",
                "Exportação em Word e PDF",
                "Exportação do gabarito",
                "10 imagens com IA por mês",
                "Suporte por WhatsApp",
            ],
            highlight: true,
        },
        {
            tipo: "MESTRE",
            title: "Mestre",
            price: "R$69,90/mês",
            badge: "Mais completo",
            desc: "Para alta demanda de criação pedagógica.",
            features: [
                "50 documentos com IA por mês",
                "Atividades, avaliações e planos de aula",
                "Alinhamento à BNCC",
                "Edição antes de exportar",
                "Exportação em Word e PDF",
                "Exportação do gabarito",
                "20 imagens com IA por mês",
                "Suporte por WhatsApp",
            ],
            highlight: false,
        },
    ];

    const PROFESSOR_EXTRAS_RISCADOS = [
        "Geração de imagens com IA",
    ];

    const painCards = [
        {
            icon: <BedtimeRoundedIcon />,
            title: "Noites virando expediente",
            desc: "Planejamento, atividades e avaliações entram no horário que deveria ser descanso.",
        },
        {
            icon: <WeekendRoundedIcon />,
            title: "Fim de semana comprometido",
            desc: "Domingo chega e ainda tem material para montar, revisar e formatar.",
        },
        {
            icon: <HomeWorkRoundedIcon />,
            title: "A escola dentro da sua casa",
            desc: "A rotina pedagógica invade seu tempo com família, projetos e cuidado pessoal.",
        },
    ];

    const solutionCards = [
        {
            icon: <TaskAltRoundedIcon />,
            title: "Gere em até 1 minuto",
            desc: "Atividades, avaliações e planos com alguns cliques.",
        },
        {
            icon: <EditNoteRoundedIcon />,
            title: "Edite tudo",
            desc: "Ajuste o conteúdo antes de aplicar com a turma.",
        },
        {
            icon: <DescriptionRoundedIcon />,
            title: "Exporte em Word",
            desc: "Leve o material em formato editável.",
        },
        {
            icon: <PictureAsPdfRoundedIcon />,
            title: "Exporte em PDF",
            desc: "Arquivo pronto para imprimir ou enviar.",
        },
    ];

    return (
        <Box
            sx={(theme) => ({
                minHeight: "100vh",
                bgcolor: "background.default",
                pt: { xs: 12.5, sm: 9.5 },
                overflowX: "hidden",
                backgroundImage:
                    theme.palette.mode === "dark"
                        ? "none"
                        : `radial-gradient(circle at 8% 6%, ${alpha(theme.palette.primary.main, 0.06)} 0, transparent 28%), radial-gradient(circle at 92% 16%, ${alpha(BRAND_TEAL, 0.06)} 0, transparent 24%)`,
            })}
        >
            <TopSalesBar onPrimaryClick={goToCadastro} />

            <Box sx={{ maxWidth: 1200, mx: "auto", px: isMobile ? 2 : 4 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: { xs: "center", sm: "flex-end" },
                        mb: { xs: 2, sm: 2.5 },
                    }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<LoginIcon />}
                        onClick={() => navigate("/login")}
                        sx={(theme) => ({
                            borderRadius: 999,
                            px: 2.4,
                            py: 1,
                            fontWeight: 850,
                            textTransform: "none",
                            bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.5 : 0.8),
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            "&:hover": {
                                borderColor: theme.palette.primary.main,
                                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.12 : 0.06),
                            },
                            mt: IS_TOPBAR && isMobile ? 4 : 0
                        })}
                    >
                        Login
                    </Button>
                </Box>

                {/* HERO */}
                <RevealOnScroll direction="up" distance={34}>
                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: { xs: 4, sm: 6 },
                            p: { xs: 2.4, sm: 4, md: 5.5 },
                            mb: { xs: 4.5, sm: 6.5 },
                            border: `1px solid ${alpha(theme.palette.divider, 0.78)}`,
                            bgcolor: "background.paper",
                            boxShadow: `0 24px 90px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.34 : 0.08)}`,
                            "&:before": {
                                content: '""',
                                position: "absolute",
                                width: 260,
                                height: 260,
                                borderRadius: "50%",
                                right: -110,
                                top: -110,
                                background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.12)} 0%, transparent 70%)`,
                                pointerEvents: "none",
                            },
                        })}
                    >
                        <Grid container spacing={{ xs: 4, md: 5 }} alignItems="center">
                            <Grid item xs={12} md={6.3}>
                                <Stack spacing={2.2} alignItems={{ xs: "center", md: "flex-start" }} textAlign={{ xs: "center", md: "left" }}>
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent={{ xs: "center", md: "flex-start" }}>
                                        <Chip
                                            icon={<WarningAmberRoundedIcon sx={{ fontSize: 16 }} />}
                                            label="Professor, seu tempo é o recurso mais valioso"
                                            sx={(theme) => ({
                                                height: 30,
                                                fontWeight: 900,
                                                color: theme.palette.warning.dark,
                                                bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.17 : 0.13),
                                                border: `1px solid ${alpha(theme.palette.warning.main, 0.22)}`,
                                                "& .MuiChip-icon": { color: theme.palette.warning.dark },
                                            })}
                                        />
                                    </Stack>

                                    <Typography
                                        sx={{
                                            fontSize: { xs: 34, sm: 46, md: 58 },
                                            lineHeight: 0.98,
                                            letterSpacing: { xs: -1.1, md: -2.2 },
                                            fontWeight: 1000,
                                            color: "text.primary",
                                        }}
                                    >
                                        Pare de perder suas noites preparando material pedagógico
                                    </Typography>

                                    <Typography
                                        sx={{
                                            maxWidth: 660,
                                            fontSize: { xs: 17, sm: 19 },
                                            lineHeight: 1.62,
                                            color: "text.secondary",
                                        }}
                                    >
                                        A Docen gera <strong>atividades, avaliações e planos de aula com IA em até 1 minuto</strong>, com conteúdo alinhado a <strong>BNCC</strong>, editável e exportável em <strong>Word e PDF</strong>. Você entra, gera, ajusta e aplica, sem transformar sua casa em extensão da escola.
                                    </Typography>

                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent={{ xs: "center", md: "flex-start" }}>
                                        {[
                                            "Fundamental 1 ao Ensino Médio"
                                        ].map((item) => (
                                            <Chip
                                                key={item}
                                                size="small"
                                                icon={<CheckCircleRoundedIcon color={BRAND_TEAL} sx={{ fontSize: 15 }} />}
                                                label={item}
                                                sx={(theme) => ({
                                                    height: 28,
                                                    fontWeight: 800,
                                                    color: BRAND_TEAL,
                                                    bgcolor: alpha(BRAND_TEAL, theme.palette.mode === "dark" ? 0.16 : 0.08),
                                                    border: `1px solid ${alpha(BRAND_TEAL, 0.16)}`,
                                                })}
                                            />
                                        ))}
                                    </Stack>

                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={1.25}
                                        sx={{ width: { xs: "100%", sm: "auto" }, pt: 0.4 }}
                                    >
                                        <Button
                                            variant="contained"
                                            size="large"
                                            endIcon={<ArrowForwardRoundedIcon />}
                                            onClick={goToCadastro}
                                            sx={{
                                                minHeight: 56,
                                                px: 3.2,
                                                borderRadius: 3.5,
                                                fontWeight: 950,
                                                fontSize: 16,
                                                textTransform: "none",
                                                bgcolor: CTA_GREEN,
                                                boxShadow: `0 16px 36px ${alpha(CTA_GREEN, 0.28)}`,
                                                "&:hover": {
                                                    bgcolor: CTA_GREEN_HOVER,
                                                    boxShadow: `0 18px 42px ${alpha(CTA_GREEN, 0.34)}`,
                                                },
                                            }}
                                        >
                                            {!isMobile ? "Começar com 1 teste gratuito" : "Começar Grátis"}
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            size="large"
                                            startIcon={<PlayArrowIcon />}
                                            onClick={scrollToPlans}
                                            sx={(theme) => ({
                                                minHeight: 56,
                                                px: 2.8,
                                                borderRadius: 3.5,
                                                fontWeight: 900,
                                                fontSize: 16,
                                                textTransform: "none",
                                                borderColor: alpha(theme.palette.primary.main, 0.35),
                                                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.08 : 0.04),
                                                "&:hover": {
                                                    borderColor: theme.palette.primary.main,
                                                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.08),
                                                },
                                            })}
                                        >
                                            Ver planos
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Grid>

                            <Grid item xs={12} md={5.7}>
                                <Box sx={{ maxWidth: 590, mx: "auto" }}>
                                    <VideoFrame
                                        title="Apresentação Docen"
                                        src={`https://player.mediadelivery.net/embed/${BUNNY_LIBRARY_ID}/${BUNNY_VIDEO_TOPO}?autoplay=false&muted=false&playsinline=true`}
                                        loaded={topVideoLoaded}
                                        onLoad={() => setTopVideoLoaded(true)}
                                        aspect={isMobile ? "1 / 1" : "16 / 9"}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </RevealOnScroll>

                {/* DOR */}
                <Box sx={{ mb: { xs: 4.5, sm: 6.5 } }}>
                    <SectionHeader
                        eyebrow="A dor que quase ninguém vê"
                        title="O expediente acaba mas o trabalho não"
                        description="A Docen foi criada para atacar justamente a parte que rouba suas noites: criar, adaptar, revisar e formatar material pedagógico."
                    />

                    <Grid container spacing={2.2}>
                        {painCards.map((item, index) => (
                            <Grid item xs={12} md={4} key={item.title}>
                                <RevealOnScroll delay={index * 120} direction="up" sx={{ height: "100%" }}>
                                    <Card
                                        elevation={0}
                                        sx={(theme) => ({
                                            height: "100%",
                                            p: 2.5,
                                            borderRadius: 4,
                                            border: `1px solid ${alpha(theme.palette.divider, 0.78)}`,
                                            bgcolor: "background.paper",
                                            boxShadow: `0 14px 38px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.24 : 0.05)}`,
                                        })}
                                    >
                                        <Stack spacing={1.4}>
                                            <Box
                                                sx={(theme) => ({
                                                    width: 44,
                                                    height: 44,
                                                    borderRadius: 3,
                                                    display: "grid",
                                                    placeItems: "center",
                                                    color: index === 0 ? theme.palette.warning.main : theme.palette.primary.main,
                                                    bgcolor: alpha(index === 0 ? theme.palette.warning.main : theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.09),
                                                })}
                                            >
                                                {React.cloneElement(item.icon, { sx: { fontSize: 24 } })}
                                            </Box>

                                            <Typography sx={{ fontWeight: 950, fontSize: 17, lineHeight: 1.18 }}>
                                                {item.title}
                                            </Typography>

                                            <Typography sx={{ color: "text.secondary", fontSize: 14.5, lineHeight: 1.52 }}>
                                                {item.desc}
                                            </Typography>
                                        </Stack>
                                    </Card>
                                </RevealOnScroll>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* SOLUÇÃO */}
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        mb: { xs: 4.5, sm: 6.5 },
                        p: { xs: 2.6, sm: 4 },
                        borderRadius: 5,
                        bgcolor: "background.paper",
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                        boxShadow: `0 18px 54px ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.08 : 0.06)}`,
                    })}
                >
                    <SectionHeader
                        eyebrow="A solução"
                        title="Material pronto para revisar, editar e aplicar"
                        description="Você informa o que precisa. A Docen gera a base. Você ajusta com autonomia e exporta do jeito certo."
                    />

                    <Grid container spacing={2}>
                        {solutionCards.map((item, index) => (
                            <Grid item xs={12} sm={6} md={3} key={item.title}>
                                <RevealOnScroll delay={index * 100} direction="up" sx={{ height: "100%" }}>
                                    <Card
                                        elevation={0}
                                        sx={(theme) => ({
                                            height: "100%",
                                            p: 2.35,
                                            borderRadius: 4,
                                            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.08 : 0.035),
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                        })}
                                    >
                                        <Stack spacing={1.35} alignItems="center" textAlign="center">
                                            <Box
                                                sx={(theme) => ({
                                                    width: 46,
                                                    height: 46,
                                                    borderRadius: 3,
                                                    display: "grid",
                                                    placeItems: "center",
                                                    color: theme.palette.primary.main,
                                                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.15 : 0.09),
                                                })}
                                            >
                                                {React.cloneElement(item.icon, { sx: { fontSize: 25 } })}
                                            </Box>

                                            <Typography sx={{ fontWeight: 950, fontSize: 16.5, lineHeight: 1.18 }}>
                                                {item.title}
                                            </Typography>

                                            <Typography sx={{ color: "text.secondary", fontSize: 14.2, lineHeight: 1.5 }}>
                                                {item.desc}
                                            </Typography>
                                        </Stack>
                                    </Card>
                                </RevealOnScroll>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                {/* COMO FUNCIONA */}
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        display: "none",
                        mb: { xs: 4.5, sm: 6.5 },
                        p: { xs: 2.6, sm: 4 },
                        borderRadius: 5,
                        bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.045 : 0.025),
                        border: `1px solid ${alpha(theme.palette.divider, 0.72)}`,
                    })}
                >
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <SectionHeader
                                align="left"
                                maxWidth={520}
                                eyebrow="Simples como deve ser"
                                title="Em poucos cliques, sua demanda sai do papel."
                                description="Escolha o tipo de material, informe série, disciplina e tema. Depois revise e exporte."
                            />
                        </Grid>

                        <Grid item xs={12} md={7}>
                            <Grid container spacing={1.5}>
                                {[
                                    "Informe o que precisa gerar.",
                                    "Revise e edite o conteúdo.",
                                    "Exporte em Word ou PDF.",
                                ].map((item, index) => (
                                    <Grid item xs={12} sm={4} key={item}>
                                        <Paper
                                            elevation={0}
                                            sx={(theme) => ({
                                                height: "100%",
                                                p: 2,
                                                borderRadius: 3.5,
                                                bgcolor: "background.paper",
                                                border: `1px solid ${alpha(theme.palette.divider, 0.72)}`,
                                            })}
                                        >
                                            <Box
                                                sx={(theme) => ({
                                                    width: 30,
                                                    height: 30,
                                                    borderRadius: 999,
                                                    display: "grid",
                                                    placeItems: "center",
                                                    mb: 1.2,
                                                    fontSize: 13,
                                                    fontWeight: 950,
                                                    color: "#fff",
                                                    bgcolor: index === 1 ? theme.palette.primary.main : CTA_GREEN,
                                                })}
                                            >
                                                {index + 1}
                                            </Box>
                                            <Typography sx={{ color: "text.secondary", lineHeight: 1.5, fontWeight: 700 }}>
                                                {item}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>

                {/* PROVA SOCIAL */}
                {DEPOIMENTOS_VIDEOS.length > 0 && (
                    <Box sx={{ mb: { xs: 4.5, sm: 6.5 } }}>
                        <SectionHeader
                            eyebrow="Professores reais"
                            title="Quem usa entende o impacto na rotina"
                            description="Depoimentos curtos de quem já viu a diferença na prática."
                        />

                        <Grid container spacing={2.4} justifyContent="center" alignItems="stretch">
                            {DEPOIMENTOS_VIDEOS.map((depoimento) => {
                                const loaded = !!depVideosLoaded[depoimento.bunny_id];

                                return (
                                    <Grid item xs={12} md={4.6} key={depoimento.bunny_id}>
                                        <Paper
                                            elevation={0}
                                            sx={(theme) => ({
                                                height: "100%",
                                                p: 2,
                                                borderRadius: 4.5,
                                                bgcolor: "background.paper",
                                                border: `1px solid ${alpha(theme.palette.divider, 0.78)}`,
                                            })}
                                        >
                                            <Typography sx={{ mb: 1.2, textAlign: "center", color: "text.secondary", fontSize: 14 }}>
                                                {depoimento.title}
                                            </Typography>

                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    aspectRatio: { xs: "9 / 16", md: "1 / 1" },
                                                    position: "relative",
                                                    bgcolor: "background.default",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        top: "50%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        height: { xs: "100%", md: "100%" },
                                                        width: { xs: "100%", md: "auto" },
                                                        aspectRatio: "1 / 1",
                                                        borderRadius: 3,
                                                        overflow: "hidden",
                                                        bgcolor: "background.default",
                                                    }}
                                                >
                                                    <VideoFrame
                                                        title="Depoimento Docen"
                                                        src={`https://player.mediadelivery.net/embed/${BUNNY_LIBRARY_ID}/${depoimento.bunny_id}?autoplay=false&muted=false`}
                                                        loaded={loaded}
                                                        onLoad={() => markDepLoaded(depoimento.bunny_id)}
                                                        squareOnDesktop
                                                        clean
                                                    />
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                )}

                {/* CHAMADA DE VALOR */}
                <RevealOnScroll direction="up" distance={32}>
                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            mb: { xs: 4.5, sm: 6.5 },
                            p: { xs: 2.8, sm: 4 },
                            borderRadius: 5,
                            color: "#fff",
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${BRAND_TEAL} 100%)`,
                            boxShadow: `0 22px 62px ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.18)}`,
                        })}
                    >
                        <Grid container spacing={2.5} alignItems="center">
                            <Grid item xs={12} md={7.5}>
                                <Typography
                                    sx={{
                                        fontSize: { xs: 24, sm: 34 },
                                        fontWeight: 1000,
                                        lineHeight: 1.08,
                                        letterSpacing: -0.9,
                                        textAlign: { xs: "center", md: "left" },
                                    }}
                                >
                                    A Docen devolve tempo sem tirar sua autonomia.
                                </Typography>
                                <Typography
                                    sx={{
                                        mt: 1,
                                        color: alpha("#fff", 0.88),
                                        fontSize: { xs: 15, sm: 16.5 },
                                        lineHeight: 1.55,
                                        textAlign: { xs: "center", md: "left" },
                                    }}
                                >
                                    Você continua decidindo, editando e personalizando. A diferença é que não precisa começar do zero toda vez.
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={4.5}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2.2,
                                        borderRadius: 4,
                                        bgcolor: alpha("#fff", 0.13),
                                        border: `1px solid ${alpha("#fff", 0.18)}`,
                                    }}
                                >
                                    <Stack spacing={1.05}>
                                        <FeatureLine color="#fff">Menos trabalho repetitivo.</FeatureLine>
                                        <FeatureLine color="#fff">Mais material pronto para revisar.</FeatureLine>
                                        <FeatureLine color="#fff">Mais tempo fora da escola.</FeatureLine>
                                    </Stack>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Paper>
                </RevealOnScroll>

                {/* PLANOS INFORMATIVOS */}
                <Box
                    id="plans-section"
                    ref={plansRef}
                    sx={{
                        mb: { xs: 4.5, sm: 6.5 },
                        scrollMarginTop: { xs: 120, sm: 96 },
                    }}
                >
                    <SectionHeader
                        eyebrow="Planos simples"
                        title="Um jeito mais leve de produzir material"
                        description="Crie sua conta, use 1 teste gratuito e assine depois, dentro da plataforma, se fizer sentido para sua rotina."
                    />

                    <Grid container spacing={2.4} justifyContent="center" alignItems="stretch">
                        {PLANS.map((plan, index) => {
                            const isProfessor = plan.tipo === "PROFESSOR";
                            const WHATSAPP = "Suporte por WhatsApp";
                            const baseSemWhatsapp = plan.features.filter((feature) => feature !== WHATSAPP);
                            const whatsappNoFinal = plan.features.includes(WHATSAPP) ? [WHATSAPP] : [];
                            const featuresToRender = isProfessor
                                ? [...baseSemWhatsapp, ...PROFESSOR_EXTRAS_RISCADOS, ...whatsappNoFinal]
                                : plan.features;

                            return (
                                <Grid item xs={12} md={4} key={plan.tipo} sx={{ display: "flex" }}>
                                    <RevealOnScroll delay={index * 120} direction="up" sx={{ width: "100%", display: "flex" }}>
                                        <Paper
                                            elevation={0}
                                            sx={(theme) => ({
                                                width: "100%",
                                                p: { xs: 2.5, sm: 3 },
                                                borderRadius: 4.5,
                                                display: "flex",
                                                flexDirection: "column",
                                                position: "relative",
                                                border: plan.highlight
                                                    ? `1.5px solid ${alpha(theme.palette.primary.main, 0.68)}`
                                                    : `1px solid ${alpha(theme.palette.divider, 0.78)}`,
                                                bgcolor: plan.highlight
                                                    ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.08 : 0.035)
                                                    : "background.paper",
                                                boxShadow: plan.highlight
                                                    ? `0 22px 58px ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.14)}`
                                                    : `0 14px 40px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.22 : 0.05)}`,
                                                transform: plan.highlight && !isMobile ? "translateY(-6px)" : "none",
                                            })}
                                        >
                                            <Stack spacing={1.2} alignItems="center" textAlign="center">
                                                <Chip
                                                    size="small"
                                                    label={plan.badge}
                                                    sx={(theme) => ({
                                                        height: 26,
                                                        fontWeight: 900,
                                                        color: plan.highlight ? "#fff" : "text.secondary",
                                                        bgcolor: plan.highlight
                                                            ? theme.palette.primary.main
                                                            : alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.08 : 0.045),
                                                    })}
                                                />

                                                <Typography sx={{ fontSize: 22, fontWeight: 1000, color: plan.highlight ? "primary.main" : "text.primary" }}>
                                                    {plan.title}
                                                </Typography>

                                                <Typography sx={{ fontSize: 28, fontWeight: 1000, letterSpacing: -0.7, color: "text.primary", lineHeight: 1 }}>
                                                    {plan.price}
                                                </Typography>

                                                <Typography sx={{ color: "text.secondary", fontSize: 14.3, lineHeight: 1.48 }}>
                                                    {plan.desc}
                                                </Typography>
                                            </Stack>

                                            <Divider sx={{ my: 2.1 }} />

                                            <Stack spacing={1.02} sx={{ flexGrow: 1 }}>
                                                {featuresToRender.map((feature) => {
                                                    const isMuted = isProfessor && PROFESSOR_EXTRAS_RISCADOS.includes(feature);

                                                    return (
                                                        <FeatureLine
                                                            key={feature}
                                                            color={plan.highlight ? "primary.main" : CTA_GREEN}
                                                            muted={isMuted}
                                                        >
                                                            {feature}
                                                        </FeatureLine>
                                                    );
                                                })}
                                            </Stack>
                                        </Paper>
                                    </RevealOnScroll>
                                </Grid>
                            );
                        })}
                    </Grid>

                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            mt: { xs: 2.5, sm: 3.4 },
                            p: { xs: 2.4, sm: 3 },
                            borderRadius: 4.5,
                            maxWidth: 840,
                            mx: "auto",
                            textAlign: "center",
                            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.10 : 0.045),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                        })}
                    >
                        <Stack spacing={1.55} alignItems="center">
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="center">
                                <Chip
                                    icon={<CreditCardRoundedIcon sx={{ fontSize: 16 }} />}
                                    size="small"
                                    label="Cartão de crédito"
                                    sx={{ fontWeight: 850 }}
                                />
                                <Chip
                                    icon={<PixIcon sx={{ fontSize: 16 }} />}
                                    size="small"
                                    label="PIX mensal"
                                    sx={{ fontWeight: 850 }}
                                />
                                <Chip
                                    icon={<AssuredWorkloadRoundedIcon sx={{ fontSize: 16 }} />}
                                    size="small"
                                    label="Assinatura dentro da plataforma"
                                    sx={{ fontWeight: 850 }}
                                />
                            </Stack>

                            <Typography sx={{ color: "text.secondary", fontSize: { xs: 14.5, sm: 16 }, lineHeight: 1.55 }}>
                                Sem escolher plano agora!<br /> Faça <strong>1 teste gratuito</strong>, veja a Docen funcionando e assine depois somente se fizer sentido.
                            </Typography>

                            <Button
                                variant="contained"
                                size="large"
                                endIcon={<ArrowForwardRoundedIcon />}
                                onClick={goToCadastro}
                                sx={{
                                    minHeight: 56,
                                    px: { xs: 3, sm: 4 },
                                    borderRadius: 3.5,
                                    fontWeight: 1000,
                                    fontSize: 16,
                                    textTransform: "none",
                                    bgcolor: CTA_GREEN,
                                    boxShadow: `0 16px 36px ${alpha(CTA_GREEN, 0.24)}`,
                                    "&:hover": { bgcolor: CTA_GREEN_HOVER },
                                }}
                            >
                                Conhecer na prática
                            </Button>

                            <Typography sx={{ color: "text.secondary", fontSize: 12.5 }}>
                                Você decide depois, dentro da plataforma.
                            </Typography>
                        </Stack>
                    </Paper>
                </Box>

                {/* GARANTIA / FECHAMENTO */}
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        display: "none",
                        mb: { xs: 5, sm: 8 },
                        p: { xs: 3, sm: 5 },
                        borderRadius: 5,
                        bgcolor: "background.paper",
                        border: `1px solid ${alpha(theme.palette.divider, 0.78)}`,
                    })}
                >
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Stack spacing={1.3} textAlign={{ xs: "center", md: "left" }}>
                                <Chip
                                    icon={<WorkspacePremiumRoundedIcon sx={{ fontSize: 16 }} />}
                                    label="Fechamento"
                                    size="small"
                                    sx={(theme) => ({
                                        alignSelf: { xs: "center", md: "flex-start" },
                                        fontWeight: 900,
                                        color: theme.palette.primary.main,
                                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.08),
                                    })}
                                />

                                <Typography
                                    sx={{
                                        fontSize: { xs: 25, sm: 36 },
                                        fontWeight: 1000,
                                        lineHeight: 1.08,
                                        letterSpacing: -1,
                                    }}
                                >
                                    Quanto mais você adia, mais uma noite sua vira planejamento.
                                </Typography>

                                <Typography sx={{ color: "text.secondary", fontSize: { xs: 15.5, sm: 17 }, lineHeight: 1.65 }}>
                                    Teste agora com uma demanda real da sua semana. Em poucos minutos você vai saber se ainda faz sentido montar tudo manualmente.
                                </Typography>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <Stack spacing={1.2} alignItems="center">
                                <Button
                                    fullWidth={isMobile}
                                    variant="contained"
                                    size="large"
                                    endIcon={<ArrowForwardRoundedIcon />}
                                    onClick={goToCadastro}
                                    sx={(theme) => (
                                        {
                                            minHeight: 58,
                                            px: 4,
                                            borderRadius: 3.5,
                                            fontWeight: 1000,
                                            fontSize: 16,
                                            textTransform: "none",
                                            bgcolor: theme.palette.primary.main,

                                        }
                                    )}
                                >
                                    Criar conta e testar grátis
                                </Button>

                                <Stack direction="row" spacing={0.8} alignItems="center" justifyContent="center">
                                    <FavoriteRoundedIcon sx={{ fontSize: 17, color: "error.main" }} />
                                    <Typography sx={{ color: "text.secondary", fontSize: 13, textAlign: "center" }}>
                                        Mais tempo para você, sem abrir mão da qualidade pedagógica.
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

            </Box>

            {/* WhatsApp */}
            <Box
                sx={{
                    position: "fixed",
                    right: { xs: 16, md: 24 },
                    bottom: { xs: 16, md: 24 },
                    zIndex: 2000,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                }}
            >
                <Fab
                    component="a"
                    href={whatsappURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="WhatsApp"
                    sx={{
                        order: 2,
                        width: 60,
                        height: 60,
                        minHeight: 0,
                        bgcolor: "#25D366",
                        color: "#fff",
                        boxShadow: 6,
                        "&:hover": { bgcolor: "#18A957" },
                        "&:hover + .waLabel, &:focus-visible + .waLabel": {
                            opacity: 1,
                            transform: "translateY(0)",
                        },
                        "&:hover .waIcon, &:focus-visible .waIcon": {
                            transform: "scale(1.14)",
                        },
                    }}
                >
                    <WhatsAppIcon
                        className="waIcon"
                        sx={{
                            fontSize: 32,
                            transition: "transform .18s ease",
                        }}
                    />
                </Fab>

                {!isMobile && (
                    <Box
                        className="waLabel"
                        sx={{
                            order: 1,
                            position: "relative",
                            bgcolor: "#0F8A4B",
                            color: "#fff",
                            px: 1.5,
                            py: 0.9,
                            borderRadius: 2,
                            fontSize: 15,
                            fontWeight: 800,
                            boxShadow: 3,
                            opacity: 0,
                            transform: "translateY(6px)",
                            transition: "all .18s ease",
                            pointerEvents: "none",
                            whiteSpace: "nowrap",
                            right: 5,
                            cursor: "default",
                            "&:after": {
                                content: '""',
                                position: "absolute",
                                right: -10,
                                top: "50%",
                                transform: "translateY(-50%)",
                                width: 0,
                                height: 0,
                                borderTop: "9px solid transparent",
                                borderBottom: "9px solid transparent",
                                borderLeft: "9px solid #0F8A4B",
                            },
                        }}
                    >
                        Entrar em contato
                    </Box>
                )}
            </Box>

            {/* Footer */}
            <Box
                component="footer"
                sx={(theme) => ({
                    mt: { xs: 5, sm: 6 },
                    py: 3,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.4 : 0.75),
                })}
            >
                <Box
                    sx={{
                        maxWidth: 1200,
                        mx: "auto",
                        px: { xs: 2, sm: 4 },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "center", sm: "flex-start" },
                        justifyContent: "space-between",
                        gap: { xs: 1.2, sm: 2 },
                        textAlign: { xs: "center", sm: "left" },
                    }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                            © {new Date().getFullYear()}{" "}
                            <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                                Docen
                            </Box>
                            . Todos os direitos reservados.
                        </Typography>

                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                            Desenvolvido no Brasil.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.4,
                            alignItems: { xs: "center", sm: "flex-end" },
                        }}
                    >
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                            <Box component="span" sx={{ color: "text.primary" }}>
                                EdenTech Soluções Tecnológicas LTDA
                            </Box>
                        </Typography>

                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                            <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                                42.153.253/0001-45
                            </Box>
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
